import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import './BudgetApproveReviewModal.css';
import FormModal from 'components/shared/modal/FormModal';
import Button from 'components/shared/button/Button';
import TasksReviewTable from 'components/admin/work-orders/details/proposals/TasksReviewTable';
import {
  getCurrentWorkOrder,
  getTasks,
} from 'selectors/admin/work-orders/work-orders';
import CostsDatesProposal from 'components/admin/work-orders/details/proposals/CostsDatesProposal';
import { getBudget } from 'selectors/admin/work-orders/details/details';
import { getCurrentUser } from 'selectors/authentication';
import { ROLES, TYPES, usersUtil } from 'services/utils/users-util';
import {
  getApprovedTasksId,
  getPdf,
  getShowApprovalConfirmation,
  getShowApproveReviewModal,
  getShowPdfViewer,
} from 'selectors/admin/work-orders/details/budget';
import numeral from 'numeral';
import QualificationsContainer from 'components/admin/work-orders/details/qualifications/QualificationsContainer';
import {
  approveBudget,
  checkTask,
  closePdfViewer,
  printBudget,
  setShowApprovalConfirmation,
  setShowApproveReviewModal,
  setShowEmailModal,
} from 'actions/admin/work-orders/details/budget';
import { BudgetStatusKeys, BudgetTaskStatusKeys } from 'domain/budget';
import { toUpperFirstLetter } from 'services/utils/text-util';
import MessageModal from 'components/shared/modal/MessageModal';
import mailSvg from 'images/mail.svg';
import printSvg from 'images/print.svg';
import PdfViewer from 'components/shared/pdf-viewer/PdfViewer';
import BudgetEmailModal from 'components/admin/work-orders/details/budget/BudgetEmailModal';
import moment from 'moment';
import { WorkOrderStatusEnum } from 'services/utils/work-order-status-enum';

function BudgetApproveReview(props) {
  const handleApprove = () => {
    if (props.onApprove) {
      props.onApprove();
    }
  };

  const handleEmail = () => {
    if (props.onEmail) {
      props.onEmail();
    }
  };

  const handlePrint = () => {
    if (props.onPrint) {
      props.onPrint();
    }
  };

  const handleTaskSelection = (id) => {
    if (props.onTaskSelection) {
      props.onTaskSelection(id);
    }
  };

  return (
    <div className="budget-approve-review">
      <TasksReviewTable
        showSelection={props.canApprove}
        values={props.values}
        onSelection={handleTaskSelection}
      />

      <div className="total-container">
        <div className="buttons">
          <Button
            className="button-white"
            icon={mailSvg}
            label="Email"
            onClick={handleEmail}
          />
          <Button
            className="button-white print-button"
            icon={printSvg}
            label="Print"
            onClick={handlePrint}
          />
        </div>

        <CostsDatesProposal total={props.total} />
      </div>

      <QualificationsContainer readOnly={true} />

      {props.canApprove && (
        <div className="button-container">
          <Button
            className="button-orange"
            disabled={
              props.values.filter((value) => value.selected).length === 0
            }
            label="Approve"
            onClick={handleApprove}
          />
        </div>
      )}
    </div>
  );
}

BudgetApproveReview.propTypes = {
  canApprove: PropTypes.bool,
  total: PropTypes.number,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      number: PropTypes.number.isRequired,
      details: PropTypes.string.isRequired,
      cost: PropTypes.number.isRequired,
      selected: PropTypes.bool,
    })
  ),
  onApprove: PropTypes.func,
  onEmail: PropTypes.func,
  onPrint: PropTypes.func,
  onTaskSelection: PropTypes.func,
};

BudgetApproveReview.defaultProps = {
  canApprove: false,
  total: 0,
  values: [],
};

class BudgetApproveReviewModal extends Component {
  render() {
    if (this.props.showApproveReviewModal) {
      const userIsClient = usersUtil.hasType(
        this.props.currentUser,
        TYPES.CLIENT
      );
      const userIsAdmin =
        usersUtil.hasRole(this.props.currentUser, ROLES.SPACKLE_ADMIN) ||
        usersUtil.hasType(this.props.currentUser, TYPES.GENERAL_CONTRACTOR);
      const woStatusEnum = WorkOrderStatusEnum.create(
        this.props.currentWorkOrder.status
      );
      const woPlm = WorkOrderStatusEnum.PLM.equals(woStatusEnum);
      const canApprove =
        this.props.budget.status === BudgetStatusKeys.WAITING &&
        (userIsClient || userIsAdmin) &&
        woPlm;
      const values = this.props.budget.budget_tasks
        .filter(
          (budgetTask) => budgetTask.status !== BudgetTaskStatusKeys.DECLINED
        )
        .map((budgetTask) => {
          const totalCost =
            numeral(budgetTask.cost).value() +
            numeral(budgetTask.fee).value() +
            numeral(budgetTask.markup).value() +
            numeral(budgetTask.insurance).value();
          const task = this.props.tasks.find(
            (task) => task.id === budgetTask.task_id
          );
          return {
            id: budgetTask.task_id,
            number: task.number,
            details: task.name !== null ? task.name : task.trade.label,
            cost: totalCost,
            selected:
              this.props.approvedTasksId.filter(
                (id) => id === budgetTask.task_id
              ).length > 0,
          };
        });

      let titlePostfix;
      if (usersUtil.hasType(this.props.currentUser, TYPES.CLIENT)) {
        titlePostfix = moment(this.props.budget.updated_at).format('LL');
      } else {
        titlePostfix = toUpperFirstLetter(this.props.budget.status);
      }
      const version =
        this.props.budget !== null
          ? `#${this.props.budget.version_number} `
          : '';
      const title = `Preliminary Estimate ${version}(WO #${
        this.props.currentWorkOrder.number
      }${BudgetApproveReviewModal.getWorkOrderName(
        this.props.currentWorkOrder
      )}) \
      - ${titlePostfix}`;

      return (
        <React.Fragment>
          <FormModal
            className="budget-approve-review-modal"
            show={this.props.showApproveReviewModal}
            showHeader={this.props.currentView !== 'thank-you'}
            title={title}
            onClose={this.handleClose}
          >
            <BudgetApproveReview
              canApprove={canApprove}
              values={values}
              total={numeral(this.props.budget.cost).value()}
              onApprove={this.handleApprove}
              onEmail={this.handleEmail}
              onPrint={this.handlePrint}
              onTaskSelection={this.handleTaskSelection}
            />
          </FormModal>

          <MessageModal
            show={this.props.showApprovalConfirmation}
            message={
              <div>
                Thank you. The Preliminary Estimate approval was submitted to
                Spackle.
              </div>
            }
            onHide={this.handleApprovalConfirmationClose}
          />

          <PdfViewer
            content={{
              source: this.props.pdf,
              type: 'blob',
            }}
            show={this.props.showPdfViewer}
            onClose={this.handlePdfViewerClose}
          />

          <BudgetEmailModal />
        </React.Fragment>
      );
    } else {
      return null;
    }
  }

  handleApprove = () => {
    this.props
      .dispatch(
        approveBudget(
          this.props.currentWorkOrder.id,
          this.props.approvedTasksId
        )
      )
      .then((response) => {
        if (response.ok) {
          this.props.dispatch(setShowApprovalConfirmation(true));
        }
      });
  };

  handleApprovalConfirmationClose = () => {
    this.props.dispatch(setShowApprovalConfirmation(false));
    this.props.dispatch(setShowApproveReviewModal(false));
  };

  handleClose = (event) => {
    event.preventDefault();
    this.props.dispatch(setShowApproveReviewModal(false));
  };

  handleEmail = () => {
    this.props.dispatch(setShowEmailModal(true));
  };

  handlePdfViewerClose = () => {
    this.props.dispatch(closePdfViewer());
  };

  handlePrint = () => {
    this.props.dispatch(printBudget(this.props.currentWorkOrder.id));
  };

  handleTaskSelection = (id) => {
    const found =
      this.props.approvedTasksId.find((taskId) => taskId === id) !== undefined;
    this.props.dispatch(checkTask(id, !found));
  };

  static getWorkOrderName(workOrder) {
    return workOrder.name !== null ? `: ${workOrder.name}` : '';
  }
}

export default connect((state) => {
  return {
    approvedTasksId: getApprovedTasksId(state),
    budget: getBudget(state),
    currentUser: getCurrentUser(state),
    currentWorkOrder: getCurrentWorkOrder(state),
    pdf: getPdf(state),
    showApprovalConfirmation: getShowApprovalConfirmation(state),
    showApproveReviewModal: getShowApproveReviewModal(state),
    showPdfViewer: getShowPdfViewer(state),
    tasks: getTasks(state),
  };
})(BudgetApproveReviewModal);



// WEBPACK FOOTER //
// ./src/components/admin/work-orders/details/budget/BudgetApproveReviewModal.js